import { useEffect, useState } from "react"
import {
  Box,
  Chip,
  Divider,
  Drawer,
  Stack,
  Typography,
  useMediaQuery
} from "@mui/material"
import { useLocation, useNavigate } from "react-router"
import { ReactComponent as Logo } from "../../assets/images/fleet-wage-logo.svg"
import { items } from "./config"
import SideNavItem from "./SideNavItem"
import Scrollbar from "./Scrollbar"
import { SIDE_NAV_WIDTH, SIDE_NAV_COLLAPSED_WIDTH } from "../constants"
import axios from "../../axios"

const SideNav = (props) => {
  const { open, onClose } = props
  const { pathname } = useLocation()
  const nav = useNavigate()
  const lgUp = useMediaQuery((theme) => theme.breakpoints.up("lg"))
  const [hovered, setHovered] = useState(false)
  const [openIssueReportsCount, setOpenIssueReportsCount] = useState(0)

  const collapsed = lgUp && !hovered

  useEffect(() => {
    let cancelled = false

    const fetchCount = () => {
      axios
        .get("/admin/issue-reports/open-count")
        .then((response) => {
          if (cancelled) return
          if (response.success) {
            setOpenIssueReportsCount(response.data?.count || 0)
          }
        })
        .catch(() => {})
    }

    fetchCount()
    const interval = setInterval(fetchCount, 60000)

    return () => {
      cancelled = true
      clearInterval(interval)
    }
  }, [pathname])

  const handleLogoClick = () => nav("/admin/organizations")

  const currentWidth = collapsed ? SIDE_NAV_COLLAPSED_WIDTH : SIDE_NAV_WIDTH

  const content = (
    <Scrollbar
      sx={{
        height: "100%",
        "& .simplebar-content": {
          height: "100%"
        },
        "& .simplebar-scrollbar:before": {
          background: "neutral.400"
        }
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%"
        }}
      >
        {/* Logo area */}
        <Box
          sx={{
            p: collapsed ? "20px 0" : "20px 24px",
            display: "flex",
            alignItems: "center",
            justifyContent: collapsed ? "center" : "flex-start",
            gap: 1.5,
            minHeight: 64,
            cursor: "pointer",
            transition: "padding 0.25s ease"
          }}
          onClick={handleLogoClick}
        >
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              height: 38,
              width: 38,
              flexShrink: 0,
              borderRadius: "10px",
              backgroundColor: "rgba(255,255,255,0.08)",
              border: "1px solid rgba(255,255,255,0.1)",
              "& svg": { width: 28, height: 28 }
            }}
          >
            <Logo />
          </Box>
          {!collapsed && (
            <Box sx={{ overflow: "hidden", whiteSpace: "nowrap" }}>
              <Typography
                variant="subtitle1"
                sx={{
                  color: "common.white",
                  fontWeight: 700,
                  fontSize: "15px",
                  lineHeight: 1.2
                }}
              >
                Fleet Wage
              </Typography>
              <Chip
                label="Admin"
                size="small"
                sx={{
                  height: 20,
                  fontSize: "10px",
                  fontWeight: 700,
                  backgroundColor: "primary.main",
                  color: "common.white",
                  mt: 0.25,
                  letterSpacing: "0.5px"
                }}
              />
            </Box>
          )}
        </Box>

        <Divider sx={{ borderColor: "rgba(255,255,255,0.08)" }} />

        {/* Navigation */}
        <Box
          component="nav"
          sx={{
            flexGrow: 1,
            px: collapsed ? 1 : 2,
            py: 2,
            transition: "padding 0.25s ease"
          }}
        >
          {items.map((section) => (
            <Box key={section.section} sx={{ mb: 2 }}>
              {!collapsed && (
                <Typography
                  variant="overline"
                  sx={{
                    color: "neutral.500",
                    fontSize: "11px",
                    fontWeight: 600,
                    letterSpacing: "0.8px",
                    px: 2,
                    py: 0.75,
                    display: "block"
                  }}
                >
                  {section.section}
                </Typography>
              )}
              {collapsed && (
                <Divider
                  sx={{
                    borderColor: "rgba(255,255,255,0.06)",
                    my: 1,
                    "&:first-of-type": { display: "none" }
                  }}
                />
              )}
              <Stack
                component="ul"
                spacing={0.25}
                sx={{ listStyle: "none", p: 0, m: 0 }}
              >
                {section.children.map((item) => {
                  const active = item.path ? pathname === item.path : false
                  const badgeCount =
                    item.path === "/admin/issue-reports" ? openIssueReportsCount : 0
                  return (
                    <SideNavItem
                      active={active}
                      disabled={item.disabled}
                      icon={item.icon}
                      key={item.title}
                      path={item.path}
                      title={item.title}
                      collapsed={collapsed}
                      badgeCount={badgeCount}
                    />
                  )
                })}
              </Stack>
            </Box>
          ))}
        </Box>

      </Box>
    </Scrollbar>
  )

  if (lgUp) {
    return (
      <Drawer
        anchor="left"
        open
        PaperProps={{
          onMouseEnter: () => setHovered(true),
          onMouseLeave: () => setHovered(false),
          sx: {
            backgroundColor: "neutral.800",
            color: "common.white",
            width: currentWidth,
            transition: "width 0.25s ease",
            overflowX: "hidden",
            borderRight: "none",
            zIndex: (theme) => theme.zIndex.drawer + 1,
            boxShadow: hovered
              ? "4px 0 24px rgba(0,0,0,0.25)"
              : "4px 0 24px rgba(0,0,0,0.12)"
          }
        }}
        variant="permanent"
      >
        {content}
      </Drawer>
    )
  }

  return (
    <Drawer
      anchor="left"
      onClose={onClose}
      open={open}
      PaperProps={{
        sx: {
          backgroundColor: "neutral.800",
          color: "common.white",
          width: SIDE_NAV_WIDTH
        }
      }}
      sx={{ zIndex: (theme) => theme.zIndex.appBar + 100 }}
      variant="temporary"
    >
      {content}
    </Drawer>
  )
}

export default SideNav
